import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { type Badge, type LevelInfo } from '../hooks/useAwardBadges';
import { cn } from '@/utils/cn';
import GemIcon from '@/components/icons/GemIcon';
import { getMe } from '@/services/authService';
import { gamificationServiceApi } from '@/services/api';

const toPersianDigits = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
const toPersianNumber = (n: number | string) => toPersianDigits(String(n));

interface AwardResultState {
  newBadges?: Badge[];
  count?: number;
  level_info?: LevelInfo | null;
}

const AchievementPage = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const queryClient = useQueryClient();
  const awardResult = location?.state?.awardResult as AwardResultState | undefined;
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Check if we're in onboarding mode
  const isOnboarding = location.pathname.startsWith('/onboarding/');

  const badges: Badge[] = Array.isArray(awardResult?.newBadges) ? awardResult!.newBadges : [];
  const level_info = awardResult?.level_info || null;

  // Current badge index
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [levelUpAnimating, setLevelUpAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialGems, setInitialGems] = useState(0);

  // Display states for level bar
  const [displayedGems, setDisplayedGems] = useState(0);
  const [displayedLevel, setDisplayedLevel] = useState(1);
  const [displayedLevelXp, setDisplayedLevelXp] = useState(0);
  const [displayedNextLevelXp, setDisplayedNextLevelXp] = useState(150);
  const [levelProgress, setLevelProgress] = useState(0);

  // Refs for tracking current values
  const currentXpRef = useRef(0);
  const currentLevelRef = useRef(1);
  const currentNextLevelXpRef = useRef(150);
  const currentGemsRef = useRef(0);
  const animationStartedRef = useRef(false);

  const currentBadge = badges[currentBadgeIndex];
  const isLastBadge = currentBadgeIndex === badges.length - 1;

  // Fetch initial gems
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const meRes = await getMe();
        const gems = meRes?.gems || 0;
        setInitialGems(gems);
      } catch {
        // Ignore error
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Initialize display states
  useEffect(() => {
    if (level_info && !isLoading) {
      setDisplayedLevel(level_info.previous_level);
      setDisplayedLevelXp(level_info.previous_level_xp);
      setDisplayedNextLevelXp(level_info.previous_next_level_xp);
      setLevelProgress(
        level_info.previous_next_level_xp > 0
          ? (level_info.previous_level_xp / level_info.previous_next_level_xp) * 100
          : 0
      );
      currentXpRef.current = level_info.previous_level_xp;
      currentLevelRef.current = level_info.previous_level;
      currentNextLevelXpRef.current = level_info.previous_next_level_xp;
    }
  }, [level_info, isLoading]);

  // Initialize gems
  useEffect(() => {
    if (initialGems > 0 && !isLoading) {
      setDisplayedGems(initialGems);
      currentGemsRef.current = initialGems;
    }
  }, [initialGems, isLoading]);

  // Helper: animate gems
  const animateGems = (amount: number): Promise<void> => {
    return new Promise((resolve) => {
      if (!amount || amount <= 0) {
        resolve();
        return;
      }
      const startGems = currentGemsRef.current;
      const endGems = startGems + amount;
      const steps = 20;
      const increment = amount / steps;
      let current = startGems;
      let step = 0;

      const interval = setInterval(() => {
        step++;
        current += increment;
        if (step >= steps) {
          currentGemsRef.current = endGems;
          setDisplayedGems(endGems);
          clearInterval(interval);
          resolve();
        } else {
          setDisplayedGems(Math.floor(current));
        }
      }, 40);
    });
  };

  // Helper: animate level progress bar smoothly
  const animateLevelProgress = (startXp: number, endXp: number, maxXp: number): Promise<void> => {
    return new Promise((resolve) => {
      const durationMs = 1500;
      const start = performance.now();

      const step = (now: number) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / durationMs);
        const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const currentXp = startXp + (endXp - startXp) * eased;
        const progress = maxXp > 0 ? (currentXp / maxXp) * 100 : 0;

        setDisplayedLevelXp(Math.round(currentXp));
        setLevelProgress(Math.min(progress, 100));

        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          currentXpRef.current = endXp;
          resolve();
        }
      };

      requestAnimationFrame(step);
    });
  };

  // Helper: animate XP with level up handling
  const animateXp = async (amount: number): Promise<void> => {
    if (!amount || amount <= 0 || !level_info) return;

    let remainingXp = amount;

    while (remainingXp > 0) {
      const currentXp = currentXpRef.current;
      const maxXp = currentNextLevelXpRef.current;
      const xpToFill = maxXp - currentXp;

      if (remainingXp >= xpToFill && currentLevelRef.current < level_info.level) {
        remainingXp -= xpToFill;
        await animateLevelProgress(currentXp, maxXp, maxXp);
        await new Promise((r) => setTimeout(r, 500));

        setLevelUpAnimating(true);
        await new Promise((r) => setTimeout(r, 600));

        currentLevelRef.current = level_info.level;
        currentXpRef.current = 0;
        currentNextLevelXpRef.current = level_info.next_level_xp;

        setDisplayedLevel(level_info.level);
        setDisplayedNextLevelXp(level_info.next_level_xp);
        setDisplayedLevelXp(0);
        setLevelProgress(0);

        await new Promise((r) => setTimeout(r, 200));
        setLevelUpAnimating(false);
        await new Promise((r) => setTimeout(r, 300));

        const totalXpAfterLevelUp = (level_info.received_xp || 0) + remainingXp;
        if (totalXpAfterLevelUp > 0) {
          await animateLevelProgress(0, totalXpAfterLevelUp, level_info.next_level_xp);
        }

        if (level_info.received_gems && level_info.received_gems > 0) {
          await animateGems(level_info.received_gems);
        }

        remainingXp = 0;
      } else {
        const newXp = currentXp + remainingXp;
        await animateLevelProgress(currentXp, newXp, currentNextLevelXpRef.current);
        remainingXp = 0;
      }
    }
  };

  // Animate current badge rewards
  useEffect(() => {
    if (isLoading || !currentBadge) return;
    if (animationStartedRef.current && currentBadgeIndex === 0) return;

    const runBadgeAnimation = async () => {
      setShowButton(false);
      await new Promise((r) => setTimeout(r, 1000)); // Wait for badge animation

      // Add badge XP
      if (currentBadge.rewardPoints > 0) {
        await animateXp(currentBadge.rewardPoints);
      }

      // Add badge gems
      if (currentBadge.rewardGems > 0) {
        await animateGems(currentBadge.rewardGems);
      }

      await new Promise((r) => setTimeout(r, 300));
      setShowButton(true);
    };

    if (currentBadgeIndex === 0) {
      animationStartedRef.current = true;
    }
    runBadgeAnimation();
  }, [currentBadgeIndex, isLoading, currentBadge]);

  // Get bookId from localStorage
  const lastLearningPathBookId = localStorage.getItem('lastLearningPathBookId');
  const bookIdFromStorage = lastLearningPathBookId ? parseInt(lastLearningPathBookId, 10) : null;
  const bookId = bookIdFromStorage && bookIdFromStorage > 0 ? bookIdFromStorage : null;

  // Helper to navigate to learning path
  const navigateToLearningPath = () => {
    // در حالت onboarding به phone برو
    if (isOnboarding) {
      navigate('/onboarding/phone', { replace: true });
      return;
    }
    
    // Invalidate queries before navigating
    if (bookId) {
      queryClient.invalidateQueries({ queryKey: ['my-sections', bookId] });
      queryClient.invalidateQueries({ queryKey: ['book-detail', bookId] });
    }
    queryClient.invalidateQueries({ queryKey: ['user-library'] });
    queryClient.invalidateQueries({ queryKey: ['quests'] });

    if (bookId) {
      navigate(`/learning/path`, { state: { bookId, showReferralInvite: true }, replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleNextBadge = async () => {
    if (!isLastBadge) {
      setCurrentBadgeIndex((prev) => prev + 1);
      return;
    }

    // Last badge - در onboarding به streak برو
    if (isOnboarding) {
      navigate('/onboarding/streak', { replace: true });
      return;
    }
    
    // Last badge - check challenges before navigating
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      const meRes = await getMe();
      const gems = meRes?.gems || 0;

      const res = await gamificationServiceApi.get<{ data: any }>('/challenges/updates');
      const payload = res.data?.data;
      const challenges = payload?.challenges || [];
      const challengeLevelInfo = payload?.level_info || null;
      const challengeBookId = payload?.book_id || null;

      // Check if any challenge has progress change
      const hasAnyProgressChange = challenges.some(
        (c: any) => c.previousProgress !== c.currentProgress
      );

      if (hasAnyProgressChange) {
        // Navigate to challenges page with data
        navigate('/learning/challenges', {
          state: {
            challenges,
            level_info: challengeLevelInfo,
            book_id: challengeBookId,
            initialGems: gems,
          },
          replace: true,
        });
      } else {
        // No progress change, go directly to learning path
        navigateToLearningPath();
      }
    } catch {
      // On error, go to learning path
      navigateToLearningPath();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#7063ff] mx-auto mb-3"></div>
          <p className="text-slate-700 font-persian text-xs">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (!currentBadge) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" dir="rtl">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full shadow-lg flex items-center justify-center p-4">
              <span className="text-4xl">🏆</span>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <p className="text-sm text-gray-800 font-persian">نشان جدیدی برای نمایش پیدا نشد.</p>
          </div>
          <button
            onClick={() => navigate('/learning/challenges', { replace: true })}
            className="mt-6 w-full bg-[#7163ff] text-white font-bold text-sm py-3 px-6 rounded-xl shadow-lg hover:bg-[#5f51ff] transition-all duration-200 font-persian"
          >
            ادامه
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-[100dvh] bg-background px-4 pt-4 flex flex-col" 
      style={isOnboarding
        ? { paddingTop: 'calc(var(--onboarding-header-h, 128px) + 24px)', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }
        : { paddingTop: '2.25rem', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      dir="rtl"
    >
      <div className="max-w-3xl mx-auto w-full flex flex-col flex-1">
        {/* Level Bar and Gems */}
        {level_info && (
          <div className="mb-7">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex h-[30px] flex-1 items-center gap-1" style={{ direction: 'ltr' }}>
                <div className="relative h-full flex-1 overflow-visible rounded-full border-2 border-white bg-gray-200 shadow-md">
                  <div
                    className="absolute bottom-0 right-0 top-0 rounded-full transition-[width] duration-[250ms] ease-out"
                    style={{
                      width: `${levelProgress}%`,
                      backgroundColor: '#FBBF24',
                      boxShadow: '0 0 6px 1px rgba(251, 191, 36, 0.4)',
                    }}
                  />
                  <div className="relative z-10 flex h-full items-center justify-center">
                    <span dir="ltr" className="pt-0.5 text-xs font-bold leading-none text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] sm:text-sm">
                      {toPersianNumber(displayedNextLevelXp)} / {toPersianNumber(displayedLevelXp)}
                    </span>
                  </div>
                </div>
                <motion.div
                  className="relative z-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#F59E0B] bg-[#FBBF24] shadow-md sm:h-[42px] sm:w-[42px]"
                  animate={levelUpAnimating ? { scale: [1, 1.25, 1] } : {}}
                  transition={{ duration: 0.3 }}
                >
                  <span className="text-sm font-black text-white drop-shadow-sm sm:text-base">{toPersianNumber(displayedLevel)}</span>
                </motion.div>
              </div>

              <div className="inline-flex h-9 shrink-0 items-center gap-1 rounded-3xl bg-cyan-500 px-2 text-white shadow-md shadow-cyan-500/30 sm:h-10">
                <span className="flex size-5 items-center justify-center"><GemIcon size={18} color="white" /></span>
                <span className="relative top-[1.5px] min-w-[2ch] text-center text-[13px] font-black leading-none sm:text-sm">{toPersianNumber(displayedGems)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Badge Content - Centered */}
        <div className="flex-1 flex flex-col justify-center translate-y-4">
          {/* Keep the current badge visible until the next badge is ready. */}
          <AnimatePresence mode="sync">
            <motion.div
              key={currentBadgeIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              className="text-center"
            >
              {/* Badge Image */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.45, ease: 'easeOut' }}
                className="mb-6 relative"
              >
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="w-[17rem] h-[17rem] bg-gradient-to-tr from-[#6f63ff24] via-[#8a83ff24] to-transparent rounded-full blur-[46px]"></div>
                </div>
                <img
                  src={currentBadge.iconUrl || ''}
                  alt={currentBadge.badgeName}
                  className="relative mx-auto w-52 h-52 sm:w-60 sm:h-60 object-contain drop-shadow-xl"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://via.placeholder.com/224x224/7163ff/FFFFFF?text=Badge';
                  }}
                />
              </motion.div>

              {/* Badge Title */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mb-3"
              >
                <div className="inline-block bg-gradient-to-r from-[#6f63ff] to-[#8a83ff] rounded-xl px-5 py-2 shadow-lg">
                  <h2 className="text-[15px] font-bold text-white font-persian">{toPersianNumber(currentBadge.badgeName)}</h2>
                </div>
              </motion.div>
{/* Progress Indicator */}
              {badges.length > 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className="mb-4"
                >
                  <div className="flex justify-center space-x-2 space-x-reverse">
                    {badges.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          'w-2.5 h-2.5 rounded-full transition-all duration-300',
                          index === currentBadgeIndex
                            ? 'bg-[#6f63ff] scale-125'
                            : index < currentBadgeIndex
                            ? 'bg-[#a7a0ff]'
                            : 'bg-gray-300'
                        )}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 font-persian mt-1.5">
                    نشان {toPersianNumber(currentBadgeIndex + 1)} از {toPersianNumber(badges.length)}
                  </p>
                </motion.div>
              )}

              {/* Action Button */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: showButton ? 1 : 0, y: showButton ? 0 : 20 }}
                transition={{ duration: 0.4 }}
                onClick={handleNextBadge}
                disabled={!showButton || isNavigating}
                className={`w-[84%] max-w-[350px] mx-auto py-3.5 rounded-xl text-center font-bold font-persian text-[15px] transition-all duration-300 active:scale-95 shadow-xl hover:shadow-2xl bg-[#6e61ff] text-white flex items-center justify-center gap-3 ${
                  !showButton || isNavigating ? 'cursor-not-allowed opacity-70' : 'hover:bg-[#5a4ed9]'
                }`}
              >
                {isNavigating && (
                  <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>{isLastBadge ? 'ادامه' : 'نشان بعدی'}</span>
              </motion.button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AchievementPage;

